import { useQuery } from '@tanstack/react-query';

import { getPrintProgress } from '@/services/main/rezept/rezeptListService';
import type { RezeptPrintProgress } from '@/types/rezept.types';

import { getStoredClinicContext } from './useClinicContext';

/**
 * Polled progress query for the レセプト発行 PDF generation tab.
 *
 * Uses `getStoredClinicContext()` (localStorage) instead of `useClinicContext()`
 * because the page runs without AuthProvider.
 *
 * Polls every 1 s until status is 'completed' or 'failed', then stops.
 */
export const useRezeptPdfProgress = (token: string | null) => {
  const ctx = getStoredClinicContext();

  const { data, isLoading, isError } = useQuery<RezeptPrintProgress>({
    queryKey: ['rezept-pdf-progress', token],
    queryFn: () => getPrintProgress(ctx!.tenant, ctx!.clinicNumber, token as string),
    enabled: !!token && !!ctx,
    refetchInterval: query => {
      const d = query.state.data as RezeptPrintProgress | undefined;
      if (d?.status === 'completed' || d?.status === 'failed') return false;
      return 1000;
    },
    // Keep polling while the tab is hidden. react-query's focusManager reports
    // `document.visibilityState !== 'hidden'`, which a separate popup window satisfied even
    // when it was not focused — a background *tab* does not. Without this, going back to the
    // レセプト発行 tab froze the job's progress until the user switched back.
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });

  return {
    status: data?.status ?? 'pending',
    completedCount: data?.completed_count ?? 0,
    totalCount: data?.total_count ?? 0,
    isComplete: data?.status === 'completed',
    isFailed: data?.status === 'failed',
    errorMessage: data?.error_message ?? null,
    failedRezeptIds: data?.failed_rezept_ids ?? null,
    isLoading,
    isError,
  };
};

export default useRezeptPdfProgress;
